import { GameService } from './game.service';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LegacyComment, IPagedResults, IPagination, Review, ReviewListItem, IOrder, IMeetingComplaint } from 'lingo2-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface IThreadDigest {
  comments_count: number;
  first: Comment[];
  is_commented: boolean;
}

interface IContentReview {
  rating: number;
  comment: string;
}

interface IComplaint {
  reason_id: number;
  reason_description: string;
  comment: string;
  screenshot?: string;
}

type reviewType = 'school' | 'teacher' | 'user-service';

export type ReviewsSortOrderType = keyof ReviewListItem;

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  public constructor(
    private http: HttpClient,
    private gameService: GameService,
  ) {
  }


  /** Поиск записей по фильтру */
  public getThreadComments(
    thread_id: string,
    filter: any,
    pagination: IPagination,
  ): Observable<IPagedResults<LegacyComment[]>> {
    const url = `https://${this.gameService.host}/web/comments/thread/${thread_id}`;
    const params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('page-size', pagination.pageSize.toString())
      .set('filter', JSON.stringify(filter));

    return this.http.get<LegacyComment[]>(url, { params, observe: 'response' }).pipe(
      map(this.handleCommentsResponse),
      // catchError(this.handleError),
    );
  }

  /** Загрузка дайджеста */
  public getThreadDigest(thread_id: string): Observable<IThreadDigest> {
    const url = `https://${this.gameService.host}/web/comments/thread/${thread_id}/digest`;
    return this.http.get<IThreadDigest>(url, { observe: 'response' }).pipe(
      map((response) => response.body!),
      // catchError(this.handleError),
    );
  }

  /** Получение комментария в нити */
  public getThreadComment(thread_id: string, comment_id: string): Observable<LegacyComment> {
    const url = `https://${this.gameService.host}/web/comments/comment/${thread_id}/${comment_id}`;
    return this.http.get<LegacyComment>(url, { observe: 'response' }).pipe(
      map(this.handleCommentResponse),
      // catchError(this.handleError),
    );
  }

  /** Удалить комментарий */
  public remove(id: string): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/comment/${id}`;
    return this.http.delete<boolean>(url, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Создать комментарий в нити */
  public createThreadComment(thread_id: string, values: Partial<LegacyComment>): Observable<LegacyComment> {
    const url = `https://${this.gameService.host}/web/comments/comment/${thread_id}`;
    return this.http.post<LegacyComment>(url, values, { observe: 'response' }).pipe(
      map(this.handleCommentResponse),
      // catchError(this.handleError),
    );
  }

  /** Поставить лайк */
  public like(id: string): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/comment/${id}/like`;
    return this.http.post<boolean>(url, {}, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Убрать лайк */
  public unLike(id: string): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/comment/${id}/like`;
    return this.http.delete<boolean>(url, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Оставить оценку контента после прохождения урока */
  public createContentReview(content_id: string, review: IContentReview): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/review/content/${content_id}`;
    return this.http.post<boolean>(url, review, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Мой отзыв на митинг */
  public getMyMeetingReview(meeting_id: string): Observable<ReviewListItem> {
    const url = `https://${this.gameService.host}/web/comments/review/meeting/${meeting_id}/my`;
    return this.http.get<ReviewListItem>(url, { observe: 'body' });
  }

  /** Список отзывов на определенную сущность(school, teacher, user-service) */
  public findReviews(
    type: reviewType,
    id: string,
    pagination?: IPagination,
    order?: IOrder<ReviewListItem>,
    commented?: boolean,
  ): Observable<IPagedResults<ReviewListItem[]>> {
    const url = `https://${this.gameService.host}/web/comments/review/${type}/${id}`;
    let params = new HttpParams();
    if (pagination) {
      params = params.set('page', pagination.page.toString()).set('page-size', pagination.pageSize.toString());
    }
    if (order) {
      order.forEach((el) => {
        params = params.set('order', el.join(':'));
      });
    }
    if (commented) {
      params = params.set('commented', commented);
    }
    return this.http.get<ReviewListItem[]>(url, { params, observe: 'response' }).pipe(map(this.handleReviewsResponse));
  }

  public findLastReviewWithoutRating() {
    const url = `https://${this.gameService.host}/web/comments/review/my-last-review-without-rating`;
    return this.http.get<ReviewListItem>(url);
  }

  /** Оставить оценку митингу */
  public updateReview(review: Review): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/review`;
    return this.http.put<boolean>(url, review, { observe: 'response' }).pipe(map((response) => response.body as any));
  }

  /** Жалоба на пользователя */
  public createUserComplaint(user_id: string, complaint: IComplaint): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/complaint/user`;
    return this.http.post<boolean>(url, { user_id, ...complaint }, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Жалоба на контент */
  public createContentComplaint(content_id: string, complaint: IComplaint): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/complaint/content`;
    return this.http.post<boolean>(url, { content_id, ...complaint }, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Жалоба на слайд урока */
  public createGameSlideComplaint(game_id: string, content_id: string, slide_id: string, complaint: IComplaint): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/complaint/game-slide`;
    return this.http.post<boolean>(url, { content_id, ...complaint }, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Жалоба на комментарий */
  public createCommentComplaint(thread_id: string, comment_id: string, complaint: IComplaint): Observable<boolean> {
    const url = `https://${this.gameService.host}/web/comments/complaint/comment`;
    return this.http.post<boolean>(url, { thread_id, comment_id, ...complaint }, { observe: 'response' }).pipe(
      map((response) => (response.body as any).result),
      // catchError(this.handleError),
    );
  }

  /** Оставить замечание митингу */
  public createMeetingComplaint(review: Partial<IMeetingComplaint>): Observable<Partial<IMeetingComplaint>> {
    const url = `https://${this.gameService.host}/web/comments/complaint/meeting`;
    return this.http
      .post<Partial<IMeetingComplaint>>(url, review, { observe: 'response' })
      .pipe(map((response) => response.body || {}));
  }

  private handleCommentResponse(response: HttpResponse<LegacyComment>): LegacyComment {
    return new LegacyComment(response.body || {});
  }

  private handleCommentsResponse(response: HttpResponse<LegacyComment[]>): IPagedResults<LegacyComment[]> {
    // const totalRecords = +res.headers.get('X-InlineCount');
    return {
      results: response.body ? response.body.map((c) => new LegacyComment(c)) : [],
      page: Number(response.headers.get('X-Pagination-Page')),
      pageSize: Number(response.headers.get('X-Pagination-PageSize')),
      total: Number(response.headers.get('X-Pagination-Total')),
      totalPages: Number(response.headers.get('X-Pagination-TotalPages')),
    };
  }

  private handleReviewsResponse(response: HttpResponse<ReviewListItem[]>): IPagedResults<ReviewListItem[]> {
    const pagination: IPagination = {
      page: Number(response.headers.get('X-Pagination-Page')),
      pageSize: Number(response.headers.get('X-Pagination-PageSize')),
      total: Number(response.headers.get('X-Pagination-Total')),
      totalPages: Number(response.headers.get('X-Pagination-TotalPages')),
    };

    return {
      results: response.body!,
      pagination,
    };
  }
}
